import Head from "next/head"
import { useRouter } from "next/router"
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_LOCALE,
  SITE_NAME,
  buildCanonicalUrl,
} from "../../seo/siteConfig"
import { JsonLdNode, buildGraph, organizationSchema, webPageSchema, websiteSchema } from "../../seo/jsonLd"

interface SkinuvitaSeoHeadProps {
  title: string
  description: string
  /** Absolute URL of the social preview image. Defaults to the site-wide one. */
  image?: string
  /** Override the canonical path (defaults to the current route, query stripped). */
  canonicalPath?: string
  /** OpenGraph object type. "article" on Ratgeber posts, "website" everywhere else. */
  ogType?: "website" | "article"
  /** Extra structured-data nodes merged into the page's @graph. */
  jsonLd?: (JsonLdNode | null | undefined)[]
  noindex?: boolean
}

/**
 * The single place every page gets its head tags from.
 *
 * Everything here is rendered server-side through next/head, so canonical,
 * social tags and JSON-LD are present in the raw HTML without JavaScript —
 * which is what non-rendering crawlers read.
 */
const SkinuvitaSeoHead = ({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  canonicalPath,
  ogType = "website",
  jsonLd = [],
  noindex = false,
}: SkinuvitaSeoHeadProps) => {
  const router = useRouter()
  const locale = router.locale || "de"
  const canonical = buildCanonicalUrl(canonicalPath ?? router.asPath, locale)

  // Only the German site is meant to be indexed. The /en/* routes are a
  // convenience translation, not content we want in search results or in the
  // sitemap, so they carry noindex regardless of what the page asks for.
  // "follow" (not "nofollow") so crawlers still traverse to the German pages,
  // and note this only works because robots.txt does NOT disallow /en —
  // a blocked crawler never reads the directive.
  const isIndexableLocale = locale === DEFAULT_LOCALE
  const shouldNoindex = noindex || !isIndexableLocale

  // Every page carries the entity records; page-specific nodes are appended.
  const graph = buildGraph([
    organizationSchema(),
    websiteSchema(),
    webPageSchema({ url: canonical, name: title, description, locale, primaryImage: image }),
    ...jsonLd,
  ])

  return (
    <Head>
      <title key="title">{title}</title>
      <meta key="description" name="description" content={description} />
      <meta
        key="robots"
        name="robots"
        content={
          shouldNoindex
            ? isIndexableLocale
              ? "noindex,nofollow"
              : "noindex,follow"
            : "index,follow,max-image-preview:large,max-snippet:-1"
        }
      />
      <link key="canonical" rel="canonical" href={canonical} />

      <meta key="og:type" property="og:type" content={ogType} />
      <meta key="og:site_name" property="og:site_name" content={SITE_NAME} />
      <meta key="og:locale" property="og:locale" content={locale === "en" ? "en_US" : "de_DE"} />
      <meta key="og:url" property="og:url" content={canonical} />
      <meta key="og:title" property="og:title" content={title} />
      <meta key="og:description" property="og:description" content={description} />
      <meta key="og:image" property="og:image" content={image} />
      <meta key="og:image:width" property="og:image:width" content={String(DEFAULT_OG_IMAGE_WIDTH)} />
      <meta key="og:image:height" property="og:image:height" content={String(DEFAULT_OG_IMAGE_HEIGHT)} />
      <meta key="og:image:alt" property="og:image:alt" content={title} />

      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta key="twitter:title" name="twitter:title" content={title} />
      <meta key="twitter:description" name="twitter:description" content={description} />
      <meta key="twitter:image" name="twitter:image" content={image} />

      <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no" />
      <link rel="icon" sizes="512x512" href="/web-app-manifest-512x512.png" />
      <link rel="icon" type="image/png" href="/favicon-16x16.png" sizes="16x16" />
      <link rel="icon" type="image/png" href="/favicon-32x32.png" sizes="32x32" />
      <link rel="icon" type="image/png" href="/favicon-96x96.png" sizes="96x96" />
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="shortcut icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <meta name="apple-mobile-web-app-title" content="Skinuvita" />
      <link rel="manifest" href="/site.webmanifest" />

      <script
        key="jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: graph }}
      />
    </Head>
  )
}

export default SkinuvitaSeoHead
